import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Terminal,
  Search as SearchIcon,
  XCircle,
  X,
  Copy,
  AlertTriangle,
  AlertCircle,
  Info,
  Activity,
  Layers,
} from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError, showSuccess, showInfo } from "../../../utils/toast";
import { Pagination } from "../../InternalScan/InternalScanDashboard/Pagination";

interface SiemensLog {
  agent_id?: number;
  asset_id?: number;
  channel?: string;
  client_id?: number;
  cluster_id?: string;
  created?: string;
  db_log?: boolean;
  error_code?: string | number;
  event_id?: string | number;
  level?: string;
  log_timestamp?: string;
  message?: string | any;
  organization_id?: number;
  platform?: string;
  raw_data?: any;
  source_id?: string;
  source_type?: string;
  _id?: string;
}

const formatLogMessage = (msg: any): string => {
  if (msg === null || msg === undefined) return "";
  if (typeof msg === "string") return msg.replace(/[ \t]+/g, " ");
  try {
    return JSON.stringify(msg).replace(/[ \t]+/g, " ");
  } catch {
    return String(msg);
  }
};

interface LogMonitorViewProps {
  activeCluster: string;
  clusterName?: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
  timeRange?: string;
  minutes?: number | string;
}

export const LogMonitorView: React.FC<LogMonitorViewProps> = ({
  activeCluster,
  clusterName,
  clientId,
  refreshKey,
  timeRange,
  minutes,
}) => {
  const clusterDisplayName = clusterName || activeCluster || "";

  // Logs Data States
  const [logs, setLogs] = useState<SiemensLog[]>([]);
  const [totalLogs, setTotalLogs] = useState<number>(0);
  const [levelsStats, setLevelsStats] = useState<Record<string, number>>({});
  const [levelsTotal, setLevelsTotal] = useState<number>(0);

  // Time window in minutes from props (DBMonitorDashboard)
  const selectedMinutes = useMemo(() => {
    if (minutes !== undefined && minutes !== null && minutes !== "") {
      return parseInt(String(minutes), 10) || 60;
    }
    if (timeRange) {
      return parseInt(timeRange, 10) || 60;
    }
    return 60;
  }, [minutes, timeRange]);

  // Loading States
  const [loadingLogs, setLoadingLogs] = useState<boolean>(false);

  // Filters
  const [selectedLevel, setSelectedLevel] = useState<string | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);

  // Pagination & Search
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Row expansion state
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  const searchDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch cluster severity levels stats: /db_monitor_dashboard/levels
  const fetchLevelsStats = useCallback(async () => {
    if (!activeCluster) {
      setLevelsStats({});
      setLevelsTotal(0);
      return;
    }

    const minutesVal = parseInt(timeRange || (selectedMinutes ? String(selectedMinutes) : "60"), 10);
    const params: any = {
      cluster_id: activeCluster,
      minutes: minutesVal,
    };

    if (clientId) {
      params.client_id = clientId;
    }

    try {
      const response = await baseapi.get("/db_monitor_dashboard/levels", { params });
      const rawData =
        response.data?.data ||
        response.data?.levels ||
        (Array.isArray(response.data) ? response.data : []);

      if (Array.isArray(rawData)) {
        const statsMap: Record<string, number> = {};
        let total = 0;
        rawData.forEach((lvl: any) => {
          if (lvl.level) {
            const count = Number(lvl.count) || 0;
            statsMap[lvl.level.toLowerCase()] = count;
            total += count;
          }
        });
        setLevelsStats(statsMap);
        setLevelsTotal(total);
      }
    } catch (err) {
      console.error("Failed to fetch levels stats:", err);
    }
  }, [activeCluster, timeRange, selectedMinutes, clientId]);

  // Fetch Logs: /db_monitor_dashboard/logs/search
  const fetchLogs = useCallback(async () => {
    if (!activeCluster) {
      setLogs([]);
      setTotalLogs(0);
      return;
    }

    setLoadingLogs(true);
    const minutesVal = parseInt(timeRange || (selectedMinutes ? String(selectedMinutes) : "60"), 10);
    const skip = (currentPage - 1) * pageSize;
    const limit = pageSize;

    const params: any = {
      cluster_id: activeCluster,
      minutes: minutesVal,
      skip,
      limit,
    };

    if (clientId) {
      params.client_id = clientId;
    }

    if (searchQuery.trim()) {
      params.search = searchQuery.trim();
    }

    if (selectedLevel) {
      params.level = selectedLevel;
    }

    if (selectedChannel) {
      params.channel = selectedChannel;
    }

    try {
      const response = await baseapi.get("/db_monitor_dashboard/logs/search", { params });
      const resData = response.data?.data || response.data;
      const logsData = Array.isArray(resData?.logs)
        ? resData.logs
        : Array.isArray(response.data?.logs)
        ? response.data.logs
        : Array.isArray(resData)
        ? resData
        : [];

      const totalCount =
        typeof resData?.total === "number"
          ? resData.total
          : typeof response.data?.total === "number"
          ? response.data.total
          : logsData.length;

      setLogs(logsData);
      setTotalLogs(totalCount);
      setExpandedRows({});
    } catch (error: any) {
      showError(error.message || "Failed to fetch logs");
      setLogs([]);
      setTotalLogs(0);
    } finally {
      setLoadingLogs(false);
    }
  }, [
    activeCluster,
    timeRange,
    selectedMinutes,
    clientId,
    currentPage,
    pageSize,
    searchQuery,
    selectedLevel,
    selectedChannel,
  ]);

  // Load stats on dependency changes
  useEffect(() => {
    fetchLevelsStats();
  }, [fetchLevelsStats, refreshKey]);

  // Load logs on dependency changes
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs, refreshKey]);

  // Handle Search Input Change with Debounce
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchInput(val);

    if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current);

    searchDebounceTimer.current = setTimeout(() => {
      setSearchQuery(val.trim());
      setCurrentPage(1);
    }, 400);
  };

  useEffect(() => {
    return () => {
      if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current);
    };
  }, []);

  const toggleRow = (idx: number) => {
    setExpandedRows((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  // Timestamp Formatter
  const formatTerminalTime = (ts?: string) => {
    if (!ts) return "-";
    try {
      const date = new Date(ts);
      const months = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
      ];
      const month = months[date.getMonth()];
      const day = date.getDate().toString().padStart(2, "0");
      const hours = date.getHours().toString().padStart(2, "0");
      const minutesStr = date.getMinutes().toString().padStart(2, "0");
      const seconds = date.getSeconds().toString().padStart(2, "0");
      return `${day}/${month} ${hours}:${minutesStr}:${seconds}`;
    } catch {
      return `${ts}`;
    }
  };

  // Severity Level Badge
  const getTerminalLevel = (level?: string) => {
    const l = (level || "").toLowerCase();
    if (l.includes("critical") || l.includes("fatal"))
      return <span className="text-red-600 dark:text-red-500 font-bold">CRITICAL</span>;
    if (l.includes("error") || l.includes("err"))
      return <span className="text-rose-600 dark:text-rose-500 font-bold">ERROR</span>;
    if (l.includes("warn"))
      return <span className="text-amber-600 dark:text-yellow-500 font-bold">WARNING</span>;
    if (l.includes("debug"))
      return <span className="text-blue-600 dark:text-blue-400 font-bold">DEBUG</span>;
    return <span className="text-cyan-600 dark:text-cyan-400 font-bold">INFO</span>;
  };

  // Compute channels available from current logs list
  const availableChannels = useMemo(() => {
    const channelMap: Record<string, number> = {};
    logs.forEach((log) => {
      if (log.channel) {
        channelMap[log.channel] = (channelMap[log.channel] || 0) + 1;
      }
    });

    return Object.entries(channelMap)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [logs]);

  // Client-side channel filtering if needed
  const displayedLogs = useMemo(() => {
    if (!selectedChannel) return logs;
    return logs.filter((l) => l.channel === selectedChannel);
  }, [logs, selectedChannel]);

  // Copy Logs to Clipboard
  const handleCopyLogs = () => {
    if (displayedLogs.length === 0) {
      showInfo("No logs to copy");
      return;
    }

    const getTerminalLevelText = (level?: string) => {
      const l = (level || "").toLowerCase();
      if (l.includes("critical") || l.includes("fatal")) return "CRITICAL";
      if (l.includes("error") || l.includes("err")) return "ERROR";
      if (l.includes("warn")) return "WARNING";
      if (l.includes("debug")) return "DEBUG";
      return "INFO";
    };

    const logsText = displayedLogs
      .map((log) => {
        const timeStr = formatTerminalTime(log.log_timestamp || log.created);
        const levelStr = getTerminalLevelText(log.level);
        const messageStr = formatLogMessage(log.message);
        return `${timeStr}: ${levelStr}: ${messageStr}`;
      })
      .join("\n");

    navigator.clipboard
      .writeText(logsText)
      .then(() => {
        showSuccess("Logs copied to clipboard");
      })
      .catch(() => {
        showError("Failed to copy logs");
      });
  };

  const totalPages = Math.max(1, Math.ceil(totalLogs / pageSize));

  // Compute level stats counts
  const criticalCount = levelsStats.critical ?? 0;
  const errorCount = levelsStats.error ?? 0;
  const warningCount = levelsStats.warning ?? 0;
  const infoCount = levelsStats.info ?? 0;
  const totalLevelsCount =
    levelsTotal > 0
      ? levelsTotal
      : criticalCount + errorCount + warningCount + infoCount;

  return (
    <div className="space-y-3 relative flex flex-col min-h-full">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-200 dark:border-gray-800 pb-3 shrink-0">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2 tracking-wide">
            <div className="p-1.5 rounded bg-orange-500 text-white">
              <Terminal className="w-4 h-4 text-white" />
            </div>
            Database Log Monitor
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Real-time database engine audit, operational &amp; severity log stream
            {clusterDisplayName && (
              <span className="ml-1 text-orange-600 dark:text-orange-400 font-mono font-semibold">
                ({clusterDisplayName})
              </span>
            )}
          </p>
        </div>
      </div>

      {/* KPI Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 shrink-0">
        {/* Card 1: Total Logs */}
        <div
          onClick={() => {
            setSelectedLevel(null);
            setSelectedChannel(null);
            setCurrentPage(1);
          }}
          className={`py-2 px-3 rounded-lg border transition-all cursor-pointer select-none flex items-center justify-between ${
            selectedLevel === null && selectedChannel === null
              ? "bg-green-50 dark:bg-slate-900/90 border-green-500/50 shadow-sm dark:shadow-md ring-1 ring-green-500/30"
              : "bg-white hover:bg-gray-50 dark:bg-[#111827]/80 dark:hover:bg-[#182234] border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 shadow-sm"
          }`}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center justify-center text-green-600 dark:text-green-400 shrink-0">
              <Activity className="w-3.5 h-3.5" />
            </div>
            <div className="truncate">
              <div className="text-[11px] font-bold text-gray-700 dark:text-gray-300">Total Logs</div>
              <div className="text-base font-extrabold text-gray-900 dark:text-white font-mono leading-tight">
                {totalLevelsCount.toLocaleString()}
              </div>
            </div>
          </div>
          {selectedLevel === null && selectedChannel === null ? (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-500/20 text-green-600 dark:text-green-400 border border-green-500/30 font-mono shrink-0">
              ALL
            </span>
          ) : (
            <span className="text-[10px] text-gray-400 dark:text-gray-500 font-mono shrink-0">Reset</span>
          )}
        </div>

        {/* Card 2: Critical */}
        <div
          onClick={() => {
            setSelectedLevel((prev) => (prev === "critical" ? null : "critical"));
            setCurrentPage(1);
          }}
          className={`py-2 px-3 rounded-lg border transition-all cursor-pointer select-none flex items-center justify-between ${
            selectedLevel === "critical"
              ? "bg-red-50 dark:bg-red-950/30 border-red-500/60 shadow-sm dark:shadow-md ring-1 ring-red-500/40"
              : "bg-white hover:bg-red-50/40 dark:bg-[#111827]/80 dark:hover:bg-[#1f1619] border-gray-200 dark:border-gray-800 hover:border-red-300 dark:hover:border-red-900/40 shadow-sm"
          }`}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-600 dark:text-red-400 shrink-0">
              <AlertCircle className="w-3.5 h-3.5" />
            </div>
            <div className="truncate">
              <div className="text-[11px] font-bold text-red-600 dark:text-red-400">Critical</div>
              <div className="text-base font-extrabold text-red-600 dark:text-red-400 font-mono leading-tight">
                {criticalCount.toLocaleString()}
              </div>
            </div>
          </div>
          {selectedLevel === "critical" && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-500/20 text-red-600 dark:text-red-400 border border-red-500/30 font-mono shrink-0">
              ACTIVE
            </span>
          )}
        </div>

        {/* Card 3: Error */}
        <div
          onClick={() => {
            setSelectedLevel((prev) => (prev === "error" ? null : "error"));
            setCurrentPage(1);
          }}
          className={`py-2 px-3 rounded-lg border transition-all cursor-pointer select-none flex items-center justify-between ${
            selectedLevel === "error"
              ? "bg-rose-50 dark:bg-rose-950/30 border-rose-500/60 shadow-sm dark:shadow-md ring-1 ring-rose-500/40"
              : "bg-white hover:bg-rose-50/40 dark:bg-[#111827]/80 dark:hover:bg-[#1f1619] border-gray-200 dark:border-gray-800 hover:border-rose-300 dark:hover:border-rose-900/40 shadow-sm"
          }`}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-600 dark:text-rose-400 shrink-0">
              <XCircle className="w-3.5 h-3.5" />
            </div>
            <div className="truncate">
              <div className="text-[11px] font-bold text-rose-600 dark:text-rose-400">Error</div>
              <div className="text-base font-extrabold text-rose-600 dark:text-rose-400 font-mono leading-tight">
                {errorCount.toLocaleString()}
              </div>
            </div>
          </div>
          {selectedLevel === "error" && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-600 dark:text-rose-400 border border-rose-500/30 font-mono shrink-0">
              ACTIVE
            </span>
          )}
        </div>

        {/* Card 4: Warnings */}
        <div
          onClick={() => {
            setSelectedLevel((prev) => (prev === "warning" ? null : "warning"));
            setCurrentPage(1);
          }}
          className={`py-2 px-3 rounded-lg border transition-all cursor-pointer select-none flex items-center justify-between ${
            selectedLevel === "warning"
              ? "bg-amber-50 dark:bg-amber-950/30 border-amber-500/60 shadow-sm dark:shadow-md ring-1 ring-amber-500/40"
              : "bg-white hover:bg-amber-50/40 dark:bg-[#111827]/80 dark:hover:bg-[#221c13] border-gray-200 dark:border-gray-800 hover:border-amber-300 dark:hover:border-amber-900/40 shadow-sm"
          }`}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-500 dark:text-amber-400 shrink-0">
              <AlertTriangle className="w-3.5 h-3.5" />
            </div>
            <div className="truncate">
              <div className="text-[11px] font-bold text-amber-600 dark:text-amber-400">Warning</div>
              <div className="text-base font-extrabold text-amber-600 dark:text-amber-400 font-mono leading-tight">
                {warningCount.toLocaleString()}
              </div>
            </div>
          </div>
          {selectedLevel === "warning" && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/30 font-mono shrink-0">
              ACTIVE
            </span>
          )}
        </div>

        {/* Card 5: Info */}
        <div
          onClick={() => {
            setSelectedLevel((prev) => (prev === "info" ? null : "info"));
            setCurrentPage(1);
          }}
          className={`py-2 px-3 rounded-lg border transition-all cursor-pointer select-none flex items-center justify-between ${
            selectedLevel === "info"
              ? "bg-cyan-50 dark:bg-cyan-950/30 border-cyan-500/60 shadow-sm dark:shadow-md ring-1 ring-cyan-500/40"
              : "bg-white hover:bg-cyan-50/40 dark:bg-[#111827]/80 dark:hover:bg-[#132026] border-gray-200 dark:border-gray-800 hover:border-cyan-300 dark:hover:border-cyan-900/40 shadow-sm"
          }`}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-600 dark:text-cyan-400 shrink-0">
              <Info className="w-3.5 h-3.5" />
            </div>
            <div className="truncate">
              <div className="text-[11px] font-bold text-cyan-600 dark:text-cyan-400">Info</div>
              <div className="text-base font-extrabold text-cyan-600 dark:text-cyan-400 font-mono leading-tight">
                {infoCount.toLocaleString()}
              </div>
            </div>
          </div>
          {selectedLevel === "info" && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-600 dark:text-cyan-400 border border-cyan-500/30 font-mono shrink-0">
              ACTIVE
            </span>
          )}
        </div>
      </div>

      {/* Linux Terminal styled container */}
      <div className="flex-1 flex flex-col min-h-[500px] bg-white dark:bg-black rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm dark:shadow-2xl overflow-hidden font-mono">
        {/* Terminal Header */}
        <div className="bg-gray-100 dark:bg-[#181a1f] px-4 py-2 border-b border-gray-200 dark:border-zinc-800 flex items-center justify-between shrink-0 select-none">
          <div className="flex items-center space-x-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f56]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#ffbd2e]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#27c93f]" />
            <span className="text-[10px] text-gray-600 dark:text-gray-400 pl-2 flex items-center gap-1.5 font-mono">
              <Terminal size={11} className="text-emerald-600 dark:text-green-500 animate-pulse" />
              logs@db-monitor:~/{clusterDisplayName ? `${clusterDisplayName}.log` : "database.log"}
            </span>
          </div>
          <div className="text-[9px] text-gray-500 dark:text-gray-400 font-mono select-none flex items-center gap-2">
            {selectedLevel && (
              <span className="text-orange-600 dark:text-orange-400 font-bold">
                level:[{selectedLevel}]
              </span>
            )}
            {selectedChannel && (
              <span className="text-cyan-600 dark:text-cyan-400 font-bold">
                channel:[{selectedChannel}]
              </span>
            )}
            <span>lines: {displayedLogs.length}</span>
            <span>| total: {totalLogs.toLocaleString()}</span>
          </div>
        </div>

        {/* Log Filter Inputs & Channels Bar */}
        <div className="p-2.5 border-b border-gray-200 dark:border-gray-900 flex flex-wrap items-center justify-between gap-3 shrink-0 bg-gray-50/80 dark:bg-zinc-950/50">
          <div className="flex flex-wrap items-center gap-3 flex-1 min-w-0">
            {/* Search Input */}
            <div className="relative w-full sm:w-72 shrink-0">
              <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                value={searchInput}
                onChange={handleSearchChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current);
                    setSearchQuery(searchInput.trim());
                    setCurrentPage(1);
                  }
                }}
                placeholder="Search logs (message, channel, source)..."
                className="w-full bg-white dark:bg-zinc-900/80 border border-gray-300 dark:border-zinc-800 rounded-lg pl-8 pr-7 py-1 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 dark:focus:border-orange-500 font-mono transition-colors"
              />
              {searchInput && (
                <button
                  onClick={() => {
                    setSearchInput("");
                    setSearchQuery("");
                    setCurrentPage(1);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer"
                >
                  <X size={11} />
                </button>
              )}
            </div>

            {/* Active Level Filter Pill */}
            {selectedLevel && (
              <div className="flex items-center gap-1 bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300 border border-orange-300 dark:border-orange-500/40 px-2 py-0.5 rounded text-[11px] font-mono">
                <span>Level: {selectedLevel.toUpperCase()}</span>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedLevel(null);
                    setCurrentPage(1);
                  }}
                  className="hover:text-orange-900 dark:hover:text-white cursor-pointer"
                >
                  <X size={11} />
                </button>
              </div>
            )}

            {/* Channels Pill Selector */}
            {availableChannels.length > 0 && (
              <div className="flex items-center gap-1.5 overflow-x-auto custom-scroll py-0.5 max-w-full">
                <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider shrink-0 flex items-center gap-1 font-mono">
                  <Layers className="w-3 h-3 text-orange-500 dark:text-orange-400" /> Channel:
                </span>
                <button
                  onClick={() => {
                    setSelectedChannel(null);
                    setCurrentPage(1);
                  }}
                  className={`px-2 py-0.5 rounded text-[10px] font-mono font-semibold transition-all shrink-0 cursor-pointer ${
                    selectedChannel === null
                      ? "bg-orange-100 dark:bg-orange-500/20 border border-orange-300 dark:border-orange-500/40 text-orange-700 dark:text-orange-300 shadow-sm"
                      : "bg-white dark:bg-zinc-900 text-gray-700 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 border border-gray-300 dark:border-zinc-800 hover:border-gray-400 dark:hover:border-zinc-700"
                  }`}
                >
                  All
                </button>
                {availableChannels.map((ch) => (
                  <button
                    key={ch.name}
                    onClick={() => {
                      setSelectedChannel((prev) => (prev === ch.name ? null : ch.name));
                      setCurrentPage(1);
                    }}
                    className={`px-2 py-0.5 rounded text-[10px] font-mono font-semibold transition-all shrink-0 flex items-center gap-1.5 cursor-pointer ${
                      selectedChannel === ch.name
                        ? "bg-orange-100 dark:bg-orange-500/20 border border-orange-300 dark:border-orange-500/40 text-orange-700 dark:text-orange-300 shadow-sm"
                        : "bg-white dark:bg-zinc-900 text-gray-700 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 border border-gray-300 dark:border-zinc-800 hover:border-gray-400 dark:hover:border-zinc-700"
                    }`}
                  >
                    <span>{ch.name}</span>
                    <span className="px-1 py-0.2 rounded bg-gray-200 dark:bg-black/50 text-[9px] text-gray-600 dark:text-gray-400 font-mono">
                      {ch.count}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right Action: Copy Logs */}
          <div className="flex items-center gap-2 select-none shrink-0">
            <button
              onClick={handleCopyLogs}
              disabled={displayedLogs.length === 0 || loadingLogs}
              className="py-1 px-2.5 bg-white hover:bg-gray-100 dark:bg-zinc-900 dark:hover:bg-zinc-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-zinc-800 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 text-[10px] font-mono hover:text-emerald-600 dark:hover:text-green-400 font-bold cursor-pointer shadow-sm"
              title="Copy terminal view to clipboard"
            >
              <Copy className="w-3 h-3" />
              <span>Copy Logs</span>
            </button>
          </div>
        </div>

        {/* Scrollable logs list container */}
        <div className="flex-1 overflow-x-auto overflow-y-auto custom-scroll min-h-0 bg-white dark:bg-black p-3 space-y-1.5 font-mono">
          {loadingLogs ? (
            <div className="py-24 text-center">
              <div className="flex flex-col items-center justify-center space-y-3">
                <div className="w-6 h-6 border-2 border-emerald-500/20 border-t-emerald-600 dark:border-t-green-500 rounded-full animate-spin"></div>
                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-600 dark:text-green-500 animate-pulse">
                  Running query...
                </span>
              </div>
            </div>
          ) : displayedLogs.length === 0 ? (
            <div className="py-24 text-center">
              <div className="flex flex-col items-center justify-center space-y-2 text-gray-400 dark:text-zinc-650">
                <XCircle size={32} className="text-gray-300 dark:text-zinc-800 mb-1" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-zinc-500">
                  No matching logs found
                </p>
                {clusterDisplayName && (
                  <p className="text-[10px] text-gray-400 dark:text-zinc-600 font-mono">
                    Cluster: {clusterDisplayName}
                  </p>
                )}
                {(selectedLevel || selectedChannel || searchQuery) && (
                  <button
                    onClick={() => {
                      setSelectedLevel(null);
                      setSelectedChannel(null);
                      setSearchInput("");
                      setSearchQuery("");
                      setCurrentPage(1);
                    }}
                    className="mt-2 px-3 py-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-orange-600 dark:text-orange-400 rounded text-xs font-semibold cursor-pointer border border-gray-300 dark:border-gray-700"
                  >
                    Clear Active Filters
                  </button>
                )}
              </div>
            </div>
          ) : (
            displayedLogs.map((log, idx) => {
              const isExpanded = !!expandedRows[idx];

              return (
                <React.Fragment key={log._id || idx}>
                  <div
                    onClick={() => toggleRow(idx)}
                    className="hover:bg-gray-100 dark:hover:bg-zinc-900/60 p-1.5 rounded transition-colors cursor-pointer text-xs leading-relaxed flex items-start"
                  >
                    <span className="text-amber-700 dark:text-amber-500/95 shrink-0 select-none mr-2 font-medium">
                      {formatTerminalTime(log.log_timestamp || log.created)}:
                    </span>
                    <span className="shrink-0 select-none mr-2 font-bold">
                      {getTerminalLevel(log.level)}:
                    </span>
                    <span className="text-emerald-800 dark:text-green-400/90 font-mono break-all whitespace-pre-wrap flex-1">
                      {formatLogMessage(log.message)}
                    </span>
                  </div>

                  {/* Expanded Details Row */}
                  {isExpanded && (
                    <div className="bg-gray-50 dark:bg-zinc-950/80 px-4 py-3 border border-gray-200 dark:border-zinc-900 rounded-lg my-1.5 font-mono shadow-sm">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-[11px] border-b border-gray-200 dark:border-zinc-900 pb-3 text-gray-600 dark:text-zinc-400">
                        <div className="flex flex-col">
                          <span className="text-[8px] font-bold text-emerald-700 dark:text-green-600 uppercase tracking-wider">
                            Channel
                          </span>
                          <span className="text-gray-800 dark:text-zinc-300 font-mono break-all mt-0.5 bg-white dark:bg-black/50 px-1.5 py-0.5 border border-gray-200 dark:border-zinc-900 rounded">
                            {log.channel || "-"}
                          </span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[8px] font-bold text-emerald-700 dark:text-green-600 uppercase tracking-wider">
                            Source Type
                          </span>
                          <span className="text-gray-800 dark:text-zinc-300 mt-0.5 font-semibold uppercase">
                            {log.source_type || "-"}
                          </span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[8px] font-bold text-emerald-700 dark:text-green-600 uppercase tracking-wider">
                            Source / Cluster ID
                          </span>
                          <span className="text-gray-800 dark:text-zinc-300 font-mono break-all mt-0.5 bg-white dark:bg-black/50 px-1.5 py-0.5 border border-gray-200 dark:border-zinc-900 rounded">
                            {log.cluster_id || log.source_id || "-"}
                          </span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[8px] font-bold text-emerald-700 dark:text-green-600 uppercase tracking-wider">
                            {log.error_code ? "Event / Error Code" : "Event ID"}
                          </span>
                          <span className="text-gray-800 dark:text-zinc-300 font-mono mt-0.5">
                            {log.event_id || log.error_code || "-"}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-col mt-3">
                        <span className="text-[8px] font-bold text-emerald-700 dark:text-green-600 uppercase tracking-wider mb-1">
                          Full Log Entry
                        </span>
                        <pre className="bg-white dark:bg-black border border-gray-200 dark:border-zinc-900 rounded-lg p-3 text-[11px] font-mono text-emerald-800 dark:text-green-400/90 leading-relaxed overflow-x-auto whitespace-pre-wrap break-all shadow-inner">
                          {(() => {
                            if (typeof log.message === "object") {
                              return JSON.stringify(log.message, null, 2);
                            }
                            try {
                              const parsed = JSON.parse(log.message || "");
                              return JSON.stringify(parsed, null, 2);
                            } catch {
                              return log.message || "-";
                            }
                          })()}
                        </pre>
                      </div>

                      {log.raw_data && (
                        <div className="flex flex-col mt-3">
                          <span className="text-[8px] font-bold text-emerald-700 dark:text-green-600 uppercase tracking-wider mb-1">
                            Raw Payload Details
                          </span>
                          <pre className="bg-white dark:bg-black border border-gray-200 dark:border-zinc-900 rounded-lg p-3 text-[10px] font-mono text-gray-800 dark:text-zinc-300 overflow-x-auto shadow-inner">
                            {JSON.stringify(log.raw_data, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </React.Fragment>
              );
            })
          )}
        </div>

        {/* Pagination Footer */}
        <div className="shrink-0 border-t border-gray-200 dark:border-zinc-900 bg-gray-50/90 dark:bg-zinc-950/80">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={pageSize}
            totalItems={totalLogs}
            onItemsPerPageChange={(val) => {
              setPageSize(val);
              setCurrentPage(1);
            }}
            itemsPerPageOptions={[25, 50, 100, 200, 500]}
          />
        </div>
      </div>
    </div>
  );
};

export default LogMonitorView;
